import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { PasswordInput } from '@/components/PasswordInput';
import { ForgotPasswordDialog } from '@/components/ForgotPasswordDialog';
import { Label } from '@/components/ui/label';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { InputOTP, InputOTPGroup, InputOTPSlot } from '@/components/ui/input-otp';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'sonner';
import { ArrowLeft, UserCircle2, Shield, Sparkles, CheckCircle2, Clock, XCircle, Loader2, IndianRupee } from 'lucide-react';
import WelcomeWhatsAppDialog from '@/components/WelcomeWhatsAppDialog';
import { loadRazorpayScript } from '@/components/RazorpayButton';
import axios from 'axios';
import { UnifiedLogin } from '@/components/auth/UnifiedLogin';
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function CustomerAuth() {
    const nav = useNavigate();
    const [params] = useSearchParams();
    const initial = params.get('tab') === 'signup' ? 'signup' : 'login';
    const [tab, setTab] = useState(initial);
    const [welcome, setWelcome] = useState(null);
    const [forgotOpen, setForgotOpen] = useState(false);

    return (
        <div className='app-shell relative min-h-screen px-4 py-6 flex flex-col'>
            <div className='hero-gradient absolute inset-0 -z-10' />
            <button onClick={() => nav('/')} className='inline-flex items-center gap-1.5 text-sm text-muted-foreground mb-4 w-fit' data-testid='auth-back-home'>
                <ArrowLeft className='w-4 h-4' /> Home
            </button>

            <div className='flex flex-col items-center text-center pt-2 pb-4 relative'>
                {/* Soft brand glow behind the portal pill — matches Admin login */}
                <div className='absolute top-16 w-72 h-24 -z-10 pointer-events-none'
                    style={{ background: 'radial-gradient(circle, rgba(249,115,22,0.35) 0%, transparent 70%)' }}
                />
                <img src='/uppower-logo.png' alt='UPPower Solar' className='h-14 w-auto mb-3 select-none' draggable='false' />
                <div className='inline-flex items-center gap-2 text-[15px] font-semibold rounded-full px-5 py-2 bg-accent/10 text-accent shadow-sm'>
                    <UserCircle2 className='w-5 h-5' />
                    Customer Portal • ग्राहक पोर्टल
                </div>
                <p className='mt-3 text-[12.5px] text-muted-foreground max-w-[280px] leading-relaxed'>
                    अपने PM Surya Ghar आवेदन, सब्सिडी और बिल तक तुरंत पहुँच पाइए।
                </p>
            </div>

            <div className='w-full max-w-md mx-auto'>
                <Card className='card-elevate border-0 shadow-lg'>
                    <CardHeader className='pb-3'>
                        <CardTitle className='font-display text-center text-[16px]'>Login or create account</CardTitle>
                        <CardDescription className='text-center text-[12px]'>ग्राहक पोर्टल • Secure login</CardDescription>
                    </CardHeader>
                    <CardContent className='px-4 sm:px-6 pt-2'>
                        <Tabs value={tab} onValueChange={setTab}>
                            {/* Alignment fix v2: `items-stretch` overrides
                                Radix's default `items-center` (which was
                                causing the active/inactive triggers to
                                measure differently and render at slightly
                                different Y positions). With stretch, both
                                triggers fill the same vertical space
                                bounded by TabsList's own p-1 padding.
                                No explicit `h-*` on children. */}
                            <TabsList className='flex w-full items-stretch [&>*]:flex-1'>
                                <TabsTrigger value='login' className='text-sm py-1.5' data-testid='auth-tab-login'>Login</TabsTrigger>
                                <TabsTrigger value='signup' className='text-sm py-1.5' data-testid='auth-tab-signup'>Signup</TabsTrigger>
                            </TabsList>
                            <TabsContent value='login' className='pt-4'>
                                <LoginTabs onWelcome={setWelcome} />
                            </TabsContent>
                            <TabsContent value='signup' className='pt-4'>
                                <SignupForm onWelcome={setWelcome} />
                            </TabsContent>
                        </Tabs>
                        <div className='mt-4 flex items-center justify-between text-xs px-1'>
                            <a href='#' onClick={(e) => { e.preventDefault(); setForgotOpen(true); }} className='text-primary hover:underline' data-testid='customer-forgot-password'>
                                Forgot password?
                            </a>
                            <a href='#' onClick={(e) => { e.preventDefault(); toast.info('Use your registered mobile to receive an OTP and access your account.'); }} className='text-muted-foreground hover:underline' data-testid='customer-forgot-account'>
                                Forgot account?
                            </a>
                        </div>
                    </CardContent>
                </Card>

                {/* Staff hint — helps users course-correct if they landed on the wrong login */}
                <button onClick={() => nav('/admin/login')} className='mt-4 w-full inline-flex items-center justify-center gap-1.5 text-[12px] text-muted-foreground'>
                    <Shield className='w-3.5 h-3.5' /> क्या आप Admin या Staff हैं? <span className='underline'>यहाँ Login करें</span>
                </button>
            </div>

            <p className='mt-auto pt-6 text-center text-[10.5px] text-muted-foreground'>
                <Sparkles className='w-3 h-3 inline text-accent mr-0.5' /> Powered by UPPower Solar • PM Surya Ghar Yojana
            </p>
            <p className='pb-2 text-center text-[10.5px] text-muted-foreground'>
                Made with <span className='text-red-500'>♥</span> in India
            </p>

            <WelcomeWhatsAppDialog data={welcome} open={!!welcome} onClose={() => { setWelcome(null); nav('/customer'); }} />
            <ForgotPasswordDialog open={forgotOpen} onClose={() => setForgotOpen(false)} />
        </div>
    );
}

function LoginTabs({ onWelcome }) {
    const nav = useNavigate();
    const [reviewInfo, setReviewInfo] = useState(null);

    const handleSuccess = (u) => {
        if (u.__signup_status === 'pending_kyc')     { nav('/customer/kyc'); return; }
        if (u.__signup_status === 'pending_payment') { nav('/customer/pending-approval'); return; }
        if (u.role === 'admin' || (u.role !== 'customer' && u.role)) nav('/admin');
        else nav('/customer');
    };

    if (reviewInfo) return <SignupReviewCard info={reviewInfo} onBack={() => setReviewInfo(null)} />;

    return (
        <div className='pt-4'>
            <UnifiedLogin
                variant='customer'
                onSuccess={handleSuccess}
                onSignupReview={setReviewInfo}
            />
        </div>
    );
}

function EmailLogin() {
    const nav = useNavigate();
    const { loginEmail, loginMobile, loading } = useAuth();
    const [identifier, setIdentifier] = useState('');   // email OR mobile
    const [password, setPassword] = useState('');
    const [reviewInfo, setReviewInfo] = useState(null);

    // Auto-detect whether the user typed an email or a mobile number
    const isMobile = /^[+\d\s\-()]{6,}$/.test(identifier) && !/@/.test(identifier);
    const digits = identifier.replace(/\D/g, '');

    const submit = async (e) => {
        e.preventDefault();
        if (!identifier || !password) { toast.error('Enter email/mobile and password'); return; }
        try {
            let u;
            if (isMobile) {
                if (digits.length < 10) { toast.error('Enter a valid 10-digit mobile'); return; }
                u = await loginMobile(digits.slice(-10), password);
            } else {
                u = await loginEmail(identifier.trim(), password);
            }
            if (u.__signup_status === 'pending_kyc') { nav('/customer/kyc'); return; }
            if (u.__signup_status === 'pending_payment') { nav('/customer/pending-approval'); return; }
            if (u.role === 'admin' || (u.role !== 'customer' && u.role)) nav('/admin');
            else nav('/customer');
            toast.success('Logged in');
        } catch (err) {
            const detail = err?.response?.data?.detail;
            if (detail && typeof detail === 'object' && (detail.code === 'signup_pending_approval' || detail.code === 'signup_rejected')) {
                setReviewInfo(detail);
                return;
            }
            toast.error(typeof detail === 'string' ? detail : (detail?.message_hi || detail?.message_en || 'Login failed'));
        }
    };
    if (reviewInfo) {
        return <SignupReviewCard info={reviewInfo} onBack={() => setReviewInfo(null)} />;
    }
    return (
        <form onSubmit={submit} className='space-y-4' data-testid='auth-email-login-form'>
            <div className='space-y-1'>
                <Label>Email or Mobile / ईमेल या मोबाइल</Label>
                <Input
                    required
                    autoComplete='username'
                    value={identifier}
                    onChange={e => setIdentifier(e.target.value)}
                    placeholder='you@example.com  or  9876543210'
                    className='min-h-[48px]'
                    data-testid='auth-email-input'
                />
                <p className='text-[11px] text-muted-foreground'>
                    {identifier && (isMobile
                        ? <span>📱 Detected as mobile — <b>{digits.slice(-10) || digits}</b></span>
                        : <span>✉️ Detected as email</span>)}
                </p>
            </div>
            <div className='space-y-1'>
                <Label>Password / पासवर्ड</Label>
                <PasswordInput required value={password} onChange={e => setPassword(e.target.value)} placeholder='Enter your password' className='min-h-[48px]' data-testid='auth-password-input' />
            </div>
            <Button type='submit' disabled={loading} className='w-full min-h-[48px]' data-testid='auth-email-submit'>{loading ? 'Signing in...' : 'Login'}</Button>
        </form>
    );
}

function OtpLogin({ onWelcome }) {
    const nav = useNavigate();
    const { requestOtp, verifyOtp, loading } = useAuth();
    const [mobile, setMobile] = useState('');
    const [step, setStep] = useState('mobile');
    const [otp, setOtp] = useState('');
    const [name, setName] = useState('');
    const [reviewInfo, setReviewInfo] = useState(null);
    const [signupFee, setSignupFee] = useState(null); // {order_id, key_id, amount_paise, token, user}

    const send = async () => {
        if (!/^\d{10}$/.test(mobile)) { toast.error('Enter 10-digit mobile'); return; }
        try { await requestOtp(mobile); setStep('otp'); toast.success('OTP sent to your mobile'); }
        catch { toast.error('Could not send OTP'); }
    };
    const verify = async () => {
        try {
            const { data } = await axios.post(`${API}/auth/verify-otp`, { mobile, otp, name: name || undefined });
            // If customer is pending_payment (fee enabled + new signup via OTP), route to fee payment
            if (data.signup_status === 'pending_payment') {
                localStorage.setItem('surya_token', data.token);
                localStorage.setItem('surya_user', JSON.stringify(data.user));
                await startSignupFeePayment({
                    token: data.token, user: data.user,
                    onDone: (nextStatus) => nav(nextStatus === 'pending_kyc' ? '/customer/kyc' : '/customer/pending-approval'),
                    onError: () => { /* stays on form */ },
                });
                return;
            }
            if (data.signup_status === 'pending_kyc') {
                localStorage.setItem('surya_token', data.token);
                localStorage.setItem('surya_user', JSON.stringify(data.user));
                nav('/customer/kyc');
                return;
            }
            await verifyOtp(mobile, otp, name || undefined);
            toast.success('Logged in');
            if (data.welcome_whatsapp) onWelcome({ ...data.welcome_whatsapp, message: '' });
            else if (data.user?.role === 'admin') nav('/admin'); else nav('/customer');
        } catch (err) {
            const detail = err?.response?.data?.detail;
            if (detail && typeof detail === 'object' && (detail.code === 'signup_pending_approval' || detail.code === 'signup_rejected')) {
                setReviewInfo(detail);
                return;
            }
            toast.error(typeof detail === 'string' ? detail : (detail?.message_hi || detail?.message_en || 'Invalid OTP'));
        }
    };

    if (reviewInfo) return <SignupReviewCard info={reviewInfo} onBack={() => setReviewInfo(null)} />;

    if (step === 'mobile') {
        return (
            <div className='space-y-4'>
                <div className='space-y-1'>
                    <Label>Mobile / मोबाइल</Label>
                    <Input value={mobile} onChange={e => setMobile(e.target.value.replace(/\D/g,'').slice(0,10))} placeholder='10-digit mobile' className='min-h-[48px]' data-testid='auth-mobile-input' />
                </div>
                <Button onClick={send} className='w-full min-h-[48px]' data-testid='auth-send-otp'>Send OTP</Button>
            </div>
        );
    }
    return (
        <div className='space-y-4'>
            <div className='space-y-1'>
                <Label>Enter OTP / OTP दर्ज करें</Label>
                <InputOTP maxLength={6} value={otp} onChange={setOtp} data-testid='auth-otp-input'>
                    <InputOTPGroup>
                        {[0,1,2,3,4,5].map(i => <InputOTPSlot index={i} key={i} />)}
                    </InputOTPGroup>
                </InputOTP>
            </div>
            <div className='space-y-1'>
                <Label>Name (optional for signup)</Label>
                <Input value={name} onChange={e => setName(e.target.value)} className='min-h-[48px]' data-testid='auth-name-input' />
            </div>
            <Button onClick={verify} disabled={loading || otp.length !== 6} className='w-full min-h-[48px]' data-testid='auth-verify-otp'>Verify & Continue</Button>
            <button className='text-xs text-muted-foreground underline' onClick={() => setStep('mobile')}>Change mobile</button>
        </div>
    );
}

// ─────────────────────────────────────────────────────────────────────────
//  Email-OTP login — great for customers whose WhatsApp/SMS is unreliable.
//  Uses the SMTP config from Admin → Notifications → Email tab.
// ─────────────────────────────────────────────────────────────────────────
function EmailOtpLogin({ onWelcome }) {
    const nav = useNavigate();
    const { requestOtpEmail, verifyOtpEmail, loading } = useAuth();
    const [email, setEmail] = useState('');
    const [step, setStep] = useState('email');
    const [otp, setOtp] = useState('');
    const [name, setName] = useState('');
    const [alsoSentToMobile, setAlsoSentToMobile] = useState(false);
    const [reviewInfo, setReviewInfo] = useState(null);

    const send = async () => {
        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) { toast.error('Enter a valid email'); return; }
        try {
            const r = await requestOtpEmail(email);
            setStep('otp');
            setAlsoSentToMobile(!!r?.has_mobile);
            toast.success(r?.has_mobile ? 'OTP sent to your email + registered mobile' : 'OTP sent to your email');
        } catch { toast.error('Could not send OTP'); }
    };

    const verify = async () => {
        try {
            await verifyOtpEmail(email, otp, name || undefined);
            toast.success('Logged in');
            nav('/customer');
        } catch (err) {
            const detail = err?.response?.data?.detail;
            if (detail && typeof detail === 'object' && (detail.code === 'signup_pending_approval' || detail.code === 'signup_rejected')) {
                setReviewInfo(detail);
                return;
            }
            toast.error(typeof detail === 'string' ? detail : 'Invalid OTP');
        }
    };

    if (reviewInfo) return <SignupReviewCard info={reviewInfo} onBack={() => setReviewInfo(null)} />;

    if (step === 'email') {
        return (
            <div className='space-y-4' data-testid='auth-email-otp-form'>
                <div className='space-y-1'>
                    <Label>Email / ईमेल</Label>
                    <Input
                        type='email'
                        value={email}
                        onChange={e => setEmail(e.target.value)}
                        placeholder='you@example.com'
                        className='min-h-[48px]'
                        data-testid='auth-email-otp-input'
                    />
                </div>
                <Button onClick={send} className='w-full min-h-[48px]' data-testid='auth-send-email-otp'>Send OTP to Email</Button>
                <p className='text-[11px] text-muted-foreground'>
                    OTP आपकी email पर आएगा। अगर आपका mobile linked है, तो WhatsApp पर भी automatic जाएगी।
                </p>
            </div>
        );
    }
    return (
        <div className='space-y-4'>
            <div className='space-y-1'>
                <Label>Enter 6-digit OTP / OTP दर्ज करें</Label>
                <InputOTP maxLength={6} value={otp} onChange={setOtp} data-testid='auth-email-otp-code'>
                    <InputOTPGroup>
                        {[0,1,2,3,4,5].map(i => <InputOTPSlot index={i} key={i} />)}
                    </InputOTPGroup>
                </InputOTP>
                <p className='text-[11px] text-muted-foreground'>
                    OTP sent to <b>{email}</b>{alsoSentToMobile ? ' + your registered mobile' : ''}.
                </p>
            </div>
            <div className='space-y-1'>
                <Label>Name (optional for signup)</Label>
                <Input value={name} onChange={e => setName(e.target.value)} className='min-h-[48px]' data-testid='auth-email-otp-name' />
            </div>
            <Button onClick={verify} disabled={loading || otp.length !== 6} className='w-full min-h-[48px]' data-testid='auth-verify-email-otp'>Verify & Continue</Button>
            <button className='text-xs text-muted-foreground underline' onClick={() => setStep('email')}>Change email</button>
        </div>
    );
}

function SignupForm({ onWelcome }) {
    const nav = useNavigate();
    const [form, setForm] = useState({ name: '', email: '', mobile: '', password: '' });
    const [loading, setLoading] = useState(false);
    const [feeInfo, setFeeInfo] = useState(null); // {enabled, fee_paise, fee_rupees, description, require_admin_approval, require_aadhaar, require_pan}
    const change = (k, v) => setForm(f => ({ ...f, [k]: v }));

    // Fetch signup fee settings on mount so we can preview the fee & KYC hint
    useEffect(() => {
        axios.get(`${API}/signup-settings`).then(({ data }) => setFeeInfo(data)).catch(() => {});
    }, []);

    const submit = async (e) => {
        e.preventDefault();
        // Enforce ALL 4 fields mandatory on the client too — matches backend
        const missing = [];
        if (!form.name.trim())     missing.push('Name');
        if (!form.email.trim())    missing.push('Email');
        if (!form.mobile.trim())   missing.push('Mobile');
        if (!form.password)        missing.push('Password');
        if (missing.length) { toast.error(`${missing.join(', ')} — यह field ज़रूरी है`); return; }
        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) { toast.error('Enter a valid email address'); return; }
        if (form.mobile.length !== 10) { toast.error('Enter a valid 10-digit mobile number'); return; }
        if (form.password.length < 6)  { toast.error('Password must be at least 6 characters'); return; }
        setLoading(true);
        try {
            const { data } = await axios.post(`${API}/auth/register`, form);
            localStorage.setItem('surya_token', data.token);
            localStorage.setItem('surya_user', JSON.stringify(data.user));

            // If signup fee is required → open Razorpay checkout
            if (data.requires_signup_fee || data.signup_status === 'pending_payment') {
                toast.info('Account created. Please complete the signup fee to continue.');
                await startSignupFeePayment({
                    token: data.token,
                    user: data.user,
                    onDone: (nextStatus) => {
                        // After payment: pending_kyc → KYC page, else → pending review page
                        nav(nextStatus === 'pending_kyc' ? '/customer/kyc' : '/customer/pending-approval');
                    },
                    onError: (msg) => { toast.error(msg || 'Payment cancelled. Please try again from Login.'); },
                });
                return;
            }

            toast.success('Account created');
            if (data.welcome_whatsapp) {
                try {
                    const { data: welc } = await axios.get(`${API}/whatsapp/welcome`, { headers: { Authorization: `Bearer ${data.token}` } });
                    onWelcome(welc);
                } catch {
                    onWelcome({ ...data.welcome_whatsapp, message: 'Welcome to UPPOWER SOLAR!' });
                }
            } else {
                nav('/customer');
            }
        } catch (err) {
            toast.error(err?.response?.data?.detail || 'Signup failed');
        } finally {
            setLoading(false);
        }
    };
    const kycHint = feeInfo && (feeInfo.require_aadhaar || feeInfo.require_pan);
    return (
        <form onSubmit={submit} className='space-y-3' data-testid='auth-signup-form'>
            {feeInfo?.enabled && feeInfo?.fee_paise > 0 && (
                <div className='rounded-xl border p-3 bg-primary/5 border-primary/20 flex items-start gap-2 text-[12px]' data-testid='signup-fee-notice'>
                    <IndianRupee className='w-4 h-4 mt-0.5 text-primary shrink-0' />
                    <div>
                        <div className='font-semibold text-foreground'>
                            One-time signup fee: ₹{feeInfo.fee_rupees.toFixed(0)}
                        </div>
                        <div className='text-muted-foreground mt-0.5 leading-snug'>
                            {feeInfo.description || 'Paid securely via Razorpay after account creation. Admin will review and approve your account.'}
                        </div>
                        {kycHint && (
                            <div className='mt-1 text-[11px] text-muted-foreground'>
                                💡 Payment के बाद login पर KYC ({feeInfo.require_aadhaar ? 'Aadhaar' : ''}{feeInfo.require_aadhaar && feeInfo.require_pan ? ' + ' : ''}{feeInfo.require_pan ? 'PAN' : ''}) माँगा जाएगा।
                            </div>
                        )}
                    </div>
                </div>
            )}
            <div className='space-y-1'>
                <Label>Name / नाम <span className='text-destructive'>*</span></Label>
                <Input required value={form.name} onChange={e => change('name', e.target.value)} placeholder='Aapka pura naam' className='min-h-[48px]' data-testid='signup-name' />
            </div>
            <div className='grid grid-cols-1 sm:grid-cols-2 gap-3'>
                <div className='space-y-1'>
                    <Label>Email <span className='text-destructive'>*</span></Label>
                    <Input required type='email' value={form.email} onChange={e => change('email', e.target.value)} placeholder='you@example.com' className='min-h-[48px]' data-testid='signup-email' />
                </div>
                <div className='space-y-1'>
                    <Label>Mobile <span className='text-destructive'>*</span></Label>
                    <Input required inputMode='numeric' value={form.mobile} onChange={e => change('mobile', e.target.value.replace(/\D/g,'').slice(0,10))} placeholder='10-digit mobile' className='min-h-[48px]' data-testid='signup-mobile' />
                </div>
            </div>
            <div className='space-y-1'>
                <Label>Password / पासवर्ड <span className='text-destructive'>*</span></Label>
                <PasswordInput required value={form.password} onChange={e => change('password', e.target.value)} placeholder='Kam se kam 6 characters' className='min-h-[48px]' data-testid='signup-password' />
                <p className='text-[11px] text-muted-foreground'>सारे fields ज़रूरी हैं। इस password से login कर पाएंगे (email या mobile दोनों से)।</p>
            </div>
            <Button type='submit' disabled={loading} className='w-full min-h-[48px]' data-testid='signup-submit'>
                {loading ? (
                    <><Loader2 className='w-4 h-4 mr-2 animate-spin' /> Creating…</>
                ) : (
                    feeInfo?.enabled && feeInfo?.fee_paise > 0
                        ? `Create Account & Pay ₹${feeInfo.fee_rupees.toFixed(0)}`
                        : 'Create Account'
                )}
            </Button>
        </form>
    );
}

/**
 * Bridge helper: creates a Razorpay order for the signup fee (using the
 * customer's freshly minted token), opens Checkout, then verifies the
 * signature server-side. On success the backend advances signup_status to
 * `pending_approval` and we navigate the user to the review screen.
 */
async function startSignupFeePayment({ token, user, onDone, onError }) {
    try {
        const Razorpay = await loadRazorpayScript();
        const { data: order } = await axios.post(
            `${API}/auth/signup-fee/order`,
            {},
            { headers: { Authorization: `Bearer ${token}` } }
        );
        if (order?.skipped) {
            // Admin disabled fee between register & order → go straight through
            onDone?.();
            return;
        }
        const rzp = new Razorpay({
            key: order.key_id,
            amount: order.amount_paise,
            currency: order.currency || 'INR',
            order_id: order.order_id,
            name: 'UPPower Solar',
            description: 'Signup verification fee',
            prefill: {
                name: user?.name || '',
                email: user?.email || '',
                contact: user?.mobile || '',
            },
            theme: { color: '#FF6B15' },
            modal: {
                ondismiss: () => onError?.('Payment cancelled'),
            },
            handler: async (response) => {
                try {
                    const { data: verifyRes } = await axios.post(
                        `${API}/auth/signup-fee/verify`,
                        {
                            razorpay_order_id: response.razorpay_order_id,
                            razorpay_payment_id: response.razorpay_payment_id,
                            razorpay_signature: response.razorpay_signature,
                        },
                        { headers: { Authorization: `Bearer ${token}` } }
                    );
                    const nextStatus = verifyRes?.signup_status || 'pending_approval';
                    toast.success('Payment successful.' + (nextStatus === 'pending_kyc' ? ' अब KYC पूरा करें।' : ' Admin approval का wait करें।'));
                    onDone?.(nextStatus);
                } catch (err) {
                    onError?.(err?.response?.data?.detail || 'Payment verification failed. Please contact support.');
                }
            },
        });
        rzp.open();
    } catch (err) {
        onError?.(err?.response?.data?.detail || err?.message || 'Could not start payment');
    }
}

/**
 * Shown inline in the login card if the customer's signup is under admin
 * review OR was rejected. Keeps the user informed without a raw error toast.
 */
function SignupReviewCard({ info, onBack }) {
    const isRejected = info?.code === 'signup_rejected';
    const Icon = isRejected ? XCircle : Clock;
    return (
        <div className='space-y-4 py-2' data-testid='signup-review-card'>
            <div className={`rounded-2xl p-5 text-center space-y-2 ${isRejected ? 'bg-destructive/5 border border-destructive/20' : 'bg-primary/5 border border-primary/20'}`}>
                <Icon className={`w-10 h-10 mx-auto ${isRejected ? 'text-destructive' : 'text-primary'}`} />
                <div className='text-[15px] font-semibold text-foreground'>
                    {isRejected ? 'Signup not approved' : 'Signup under review'}
                </div>
                <div className='text-[13px] text-muted-foreground leading-relaxed'>
                    {info?.message_hi}
                </div>
                <div className='text-[12px] text-muted-foreground leading-relaxed'>
                    {info?.message_en}
                </div>
                {info?.reject_reason && (
                    <div className='mt-2 text-[12px] rounded-lg bg-white/70 border p-2 text-left'>
                        <span className='font-semibold'>Reason:</span> {info.reject_reason}
                    </div>
                )}
                {info?.name && (
                    <div className='text-[12px] text-muted-foreground pt-1'>
                        {isRejected ? 'Applicant' : 'Namaste'}: <span className='font-medium'>{info.name}</span>
                    </div>
                )}
            </div>
            <Button variant='outline' className='w-full' onClick={onBack} data-testid='signup-review-back'>
                Back to Login
            </Button>
        </div>
    );
}
